import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Download, Eye } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDownloadCounts } from "@/hooks/use-download-counts";
import { trackEvent } from "@/lib/analytics";
import { type Printable } from "@/data/printables";
import PrintablePreviewDialog from "./PrintablePreviewDialog";
import JsonLd from "./JsonLd";

interface Props {
  items: Printable[];
  baseUrl?: string;
}

const PrintableGallery = ({ items, baseUrl = "https://queebi.com" }: Props) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { counts, increment } = useDownloadCounts();
  const [previewing, setPreviewing] = useState<Printable | null>(null);

  const handlePreview = (p: Printable) => {
    trackEvent("printable_preview", "engagement", p.slug);
    if (isMobile) {
      window.open(p.pdf, "_blank", "noopener,noreferrer");
    } else {
      setPreviewing(p);
    }
  };

  const handleDownload = (p: Printable) => {
    trackEvent("file_download", "download", p.slug);
    increment(p.slug);
  };

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: items.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "CreativeWork",
        name: t(p.titleKey),
        description: t(p.descriptionKey),
        url: `${baseUrl}${p.pdf}`,
        image: `${baseUrl}${p.cover}`,
        encodingFormat: "application/pdf",
      },
    })),
  };

  return (
    <>
      <JsonLd data={itemListSchema} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {items.map((p) => {
          const title = t(p.titleKey);
          const description = t(p.descriptionKey);
          const count = counts[p.slug] ?? 0;
          const titleId = `printable-${p.slug}-title`;
          return (
            <article
              key={p.slug}
              aria-labelledby={titleId}
              className="bg-background rounded-2xl shadow-md overflow-hidden flex flex-col transition-transform duration-300 hover:scale-[1.02] focus-within:ring-2 focus-within:ring-primary"
            >
              <button
                type="button"
                onClick={() => handlePreview(p)}
                aria-label={`${t("activities.printables.previewAria", { title, pages: p.pages })}`}
                className="block bg-muted relative group focus:outline-none"
              >
                <img
                  src={p.cover}
                  alt={description}
                  width={600}
                  height={800}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto aspect-[3/4] object-cover"
                />
                <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-background/95 text-foreground rounded-full px-4 py-2 text-sm font-bold flex items-center gap-2 shadow-lg">
                    <Eye className="h-4 w-4" aria-hidden="true" />
                    {t("activities.printables.preview")}
                  </span>
                </span>
              </button>
              <div className="p-5 flex flex-col gap-3 flex-1">
                <h3 id={titleId} className="font-heading font-bold text-lg leading-snug">
                  {title}
                </h3>
                <p className="text-sm text-muted-foreground line-clamp-2">{description}</p>
                <div className="mt-auto flex items-center justify-between gap-3 pt-2">
                  <Button
                    asChild
                    size="sm"
                    className="rounded-full bg-primary text-primary-foreground font-bold"
                  >
                    <a
                      href={p.pdf}
                      download={`queebi-${p.slug}.pdf`}
                      aria-label={t("activities.printables.downloadAria", { title, pages: p.pages })}
                      rel="noopener"
                      onClick={() => handleDownload(p)}
                    >
                      <Download className="mr-2 h-4 w-4" aria-hidden="true" />
                      {t("activities.printables.download")}
                    </a>
                  </Button>
                  <span className="text-sm text-muted-foreground" aria-live="polite">
                    {t("activities.printables.downloads", { count })}
                  </span>
                </div>
              </div>
            </article>
          );
        })}
      </div>
      <PrintablePreviewDialog
        printable={previewing}
        onClose={() => setPreviewing(null)}
        onDownload={handleDownload}
      />
    </>
  );
};

export default PrintableGallery;
